import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { MarketingNav } from "./MarketingNav";
import { MarketingFooter } from "./MarketingFooter";
import { Seo } from "./Seo";
import { FaqSection } from "./Faq";
import { Reveal } from "./anim";
import { IMAGES, CONTACT, SINCE, ABOUT_FAQ, breadcrumb, faqSchema } from "./marketingData";

export default function AboutPage() {
  return (
    <div className="bg-[#F9F8F6] text-[#1A1A18] font-body antialiased overflow-x-hidden" data-testid="about-page">
      <Seo
        title={`About Indian Transport Organisation — ODC & Heavy-haul Specialists since ${SINCE}`}
        description="Since 1972, Indian Transport Organisation has moved over-dimensional cargo, transformers, turbines, machinery, oil & gas modules and defence equipment across India — one Lorry Receipt, one accountable partner."
        path="/about"
        image={IMAGES.odcTransformer}
        imageAlt="Transformer on a heavy-haul trailer on an Indian highway"
        keywords="about indian transport organisation, transport company mumbai, odc heavy haul transporter india, logistics company since 1972"
        jsonLd={[
          {
            "@context": "https://schema.org", "@type": "Organization",
            name: "Indian Transport Organisation", url: "https://itoworldwide.com",
            logo: "https://itoworldwide.com/ito-logo.png", foundingDate: "1972",
            description: "ODC, heavy-haul and defence logistics across India since 1972.",
          },
          breadcrumb([{ name: "Home", path: "/" }, { name: "About", path: "/about" }]),
          faqSchema(ABOUT_FAQ),
        ]}
      />
      <MarketingNav heroDark />

      <section className="relative pt-36 pb-16 bg-[#1A1A18] text-white overflow-hidden">
        <img src={IMAGES.odcTransformer} alt="Transformer on a heavy-haul trailer on an Indian highway" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8">
          <p className="text-xs font-semibold tracking-widest uppercase text-[#D4A373] mb-3">About us · since {SINCE}</p>
          <h1 className="font-heading font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.03] max-w-3xl">Over-dimensional cargo is our home ground.</h1>
          <p className="mt-5 text-white/80 max-w-2xl leading-relaxed">
            For over five decades, Indian Transport Organisation has moved the loads others won't touch —
            transformers, turbines, excavators, oil &amp; gas modules, storage tanks and battle tanks —
            on specialised low-bed and modular trailers, route-surveyed and escorted across India.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-8">
          {[
            { img: IMAGES.odcExcavator, h: "Construction machinery", p: "Excavators & plant on low-bed trailers" },
            { img: IMAGES.airSuspension, h: "Air-suspension transit", p: "Shock-free rides for sensitive machinery" },
          ].map((c, i) => (
            <Reveal key={c.h} delay={i * 0.1} className="relative rounded-2xl overflow-hidden h-[420px]">
              <img src={c.img} alt={c.h} className="w-full h-full object-cover" />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#1A1A18]/70 to-transparent" />
              <div className="absolute bottom-6 left-6">
                <div className="font-heading font-bold text-white text-2xl">{c.h}</div>
                <div className="text-white/85 text-sm">{c.p}</div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="max-w-3xl mx-auto px-5 sm:px-8 mt-16 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl mb-4">One partner, accountable end to end.</h2>
          <p className="text-[#4F4C44] leading-relaxed">
            From a single machine spare to a fleet of tanks, we plan, permit, escort and deliver —
            by road or air — to any pincode in India, on one Lorry Receipt.
          </p>
          <Link to="/contact" data-testid="about-contact-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#C84B31] px-7 py-3 text-sm font-semibold text-white hover:bg-[#a83c26] transition-colors">
            Talk to our team <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <FaqSection faqs={ABOUT_FAQ} title="About ITO — frequently asked questions" testid="about-faq" />

      <MarketingFooter />
    </div>
  );
}
